import Locales from 'locales';

const L = Locales.invoice.ecobank;

export const ECOBANK_PRESENTATION = Object.freeze({
  SETUP_REQUIRED: 'SETUP_REQUIRED',
  PROFILE_UNKNOWN: 'PROFILE_UNKNOWN',
  PROFILE_EMAIL_CODE: 'PROFILE_EMAIL_CODE',
  PROFILE_QR: 'PROFILE_QR',
  PROFILE_ACTIVATION: 'PROFILE_ACTIVATION',
  PROFILE_COMPLETION: 'PROFILE_COMPLETION',
  PAYMENT_REVIEW: 'PAYMENT_REVIEW',
  PAYMENT_PROCESSING: 'PAYMENT_PROCESSING',
  PAYMENT_AUTHORIZATION: 'PAYMENT_AUTHORIZATION',
  PAYMENT_CONFIRMED: 'PAYMENT_CONFIRMED',
  PAYMENT_REJECTED: 'PAYMENT_REJECTED',
  PAYMENT_UNRESOLVED: 'PAYMENT_UNRESOLVED',
  UNAVAILABLE: 'UNAVAILABLE',
});

const PROCESSING_ATTEMPT_STATUSES = new Set([
  'PREPARING',
  'QUEUED',
  'SUBMITTING',
  'VALIDATING',
  'AWAITING_CONFIRMATION',
]);

export const getEcobankAttempt = (paymentView) => paymentView?.active_attempt || paymentView?.attempt || null;

export const resolveEcobankProfilePresentation = (paymentView) => {
  const profileStatus = paymentView?.customer_profile?.status;
  const actions = paymentView?.actions || {};

  if (profileStatus === 'UNKNOWN' && actions.can_request_profile_otp === true) {
    return ECOBANK_PRESENTATION.PROFILE_UNKNOWN;
  }

  if (profileStatus === 'ACTIVE' && actions.can_initiate === true) return ECOBANK_PRESENTATION.PAYMENT_REVIEW;
  if (profileStatus === 'OTP_SENT' && actions.can_validate_profile_otp === true) {
    return ECOBANK_PRESENTATION.PROFILE_EMAIL_CODE;
  }
  if (profileStatus === 'CREATED' && actions.can_activate_profile === true) {
    return paymentView.customer_profile.has_qr_code
      ? ECOBANK_PRESENTATION.PROFILE_QR
      : ECOBANK_PRESENTATION.PROFILE_ACTIVATION;
  }
  if (profileStatus === 'ACTIVATION_FAILED' && actions.can_activate_profile === true) {
    return ECOBANK_PRESENTATION.PROFILE_ACTIVATION;
  }
  if (profileStatus === 'ACTIVATED' && actions.can_complete_profile === true)
    return ECOBANK_PRESENTATION.PROFILE_COMPLETION;
  if (
    (!paymentView?.customer_profile || ['NOT_CREATED', 'OTP_SEND_FAILED'].includes(profileStatus)) &&
    actions.can_request_profile_otp === true
  ) {
    return ECOBANK_PRESENTATION.SETUP_REQUIRED;
  }

  return ECOBANK_PRESENTATION.UNAVAILABLE;
};

export const resolveEcobankPresentation = (paymentView) => {
  if (!paymentView?.available) return ECOBANK_PRESENTATION.UNAVAILABLE;

  const attempt = getEcobankAttempt(paymentView);
  const attemptStatus = attempt?.status;
  if (attemptStatus === 'AWAITING_OTP' && (paymentView.actions?.can_submit_otp === true || attempt?.cooldown_until)) {
    return ECOBANK_PRESENTATION.PAYMENT_AUTHORIZATION;
  }
  if (attemptStatus === 'AWAITING_OTP') return ECOBANK_PRESENTATION.UNAVAILABLE;
  if (PROCESSING_ATTEMPT_STATUSES.has(attemptStatus)) return ECOBANK_PRESENTATION.PAYMENT_PROCESSING;
  if (attemptStatus === 'CONFIRMED') return ECOBANK_PRESENTATION.PAYMENT_CONFIRMED;
  if (attemptStatus === 'FAILED') return ECOBANK_PRESENTATION.PAYMENT_REJECTED;
  if (attemptStatus === 'UNKNOWN') return ECOBANK_PRESENTATION.PAYMENT_UNRESOLVED;

  return resolveEcobankProfilePresentation(paymentView);
};

export const getEcobankListPresentation = (invoice, currentView = null) => {
  const summary = invoice?.ecobank_payment;
  const currentAttempt = getEcobankAttempt(currentView);
  const listedAttemptId = summary?.attempt_id || invoice?.active_ecobank_attempt?.id;
  const hasDifferentActiveAttempt = Boolean(
    currentView &&
      listedAttemptId &&
      listedAttemptId !== currentAttempt?.id &&
      (summary?.mutation_blocked || invoice?.active_ecobank_attempt)
  );
  // A cached view cannot unlock another attempt started in a different tab.
  const paymentView = hasDifferentActiveAttempt ? null : currentView;
  const attempt = getEcobankAttempt(paymentView);
  const status = attempt?.status || summary?.status || invoice?.active_ecobank_attempt?.status || null;
  const isLocked =
    typeof paymentView?.actions?.can_delete_invoice === 'boolean'
      ? !paymentView.actions.can_delete_invoice
      : Boolean(summary?.mutation_blocked || invoice?.active_ecobank_attempt || attempt?.is_active);

  if (status === 'AWAITING_OTP') {
    return {
      actionLabel: L.action.continue_payment,
      canOpen: true,
      isLocked: true,
    };
  }

  if (status === 'UNKNOWN') {
    return {
      actionLabel: L.action.refresh,
      canOpen: true,
      isLocked: true,
    };
  }

  if (status === 'CONFIRMED') {
    return {
      actionLabel: null,
      canOpen: false,
      isLocked: false,
    };
  }

  if (PROCESSING_ATTEMPT_STATUSES.has(status)) {
    return {
      actionLabel: L.action.refresh,
      canOpen: true,
      isLocked: true,
    };
  }

  const profilePresentation = resolveEcobankProfilePresentation(paymentView);
  let actionLabel = paymentView ? L.action.refresh : L.action.pay;
  if (
    profilePresentation === ECOBANK_PRESENTATION.PROFILE_EMAIL_CODE ||
    profilePresentation === ECOBANK_PRESENTATION.PROFILE_QR ||
    profilePresentation === ECOBANK_PRESENTATION.PROFILE_ACTIVATION ||
    profilePresentation === ECOBANK_PRESENTATION.PROFILE_COMPLETION
  ) {
    actionLabel = L.action.continue_setup;
  } else if (
    profilePresentation === ECOBANK_PRESENTATION.SETUP_REQUIRED ||
    profilePresentation === ECOBANK_PRESENTATION.PAYMENT_REVIEW
  ) {
    actionLabel = L.action.pay;
  }

  return { actionLabel, canOpen: true, isLocked };
};
